import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import Arrow from '@/assets/svg/arrow-down.svg';
import { ScrollStyles, TextLimit } from '@/components/styles';
import StockColorPicker from '@/components/StockColorPicker';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import CrossIcon from '@/assets/svg/close-12.svg';

import { StockColorPickerSelectProps, ColorOptionType } from './types';

function StockColorPickerSelect({
  label,
  colors,
  selectedColors,
  onChange,
  onClear,
}: StockColorPickerSelectProps) {
  const [isOpen, setOpened] = useState(false);
  const componentRef = useRef<HTMLDivElement | null>(null);

  useOnClickOutside(componentRef, () => {
    if (isOpen) {
      setOpened(false);
    }
  });

  const handleOpenSelect = () => {
    setOpened(!isOpen);
  };

  const handleColorClick = (color: ColorOptionType) => {
    onChange?.(color);
  };

  const handleClear = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    onClear?.();
  };

  return (
    <Component>
      {label && <Label>{label}</Label>}

      <SelectWrapper ref={componentRef}>
        <Select onClick={handleOpenSelect}>
          <Picked>
            {selectedColors &&
              selectedColors.length > 0 &&
              selectedColors.map((item) => item.label).join(', ')}
          </Picked>

          <Widgets>
            {selectedColors && selectedColors.length > 0 && (
              <PickedLabel>Выбрано {selectedColors.length}</PickedLabel>
            )}
            {onClear && selectedColors && selectedColors.length > 0 && (
              <ClearButton onClick={handleClear}>
                <CrossIcon />
              </ClearButton>
            )}
            <IconWrapper isOpen={isOpen}>
              <Arrow />
            </IconWrapper>
          </Widgets>
        </Select>
        <Content isOpen={isOpen}>
          <StockColorPicker
            colors={colors}
            selectedColors={selectedColors}
            onChange={(color) => handleColorClick(color)}
          />
        </Content>
      </SelectWrapper>
    </Component>
  );
}

export default StockColorPickerSelect;

const Component = styled.div`
  position: relative;
  width: 100%;
`;

const Label = styled.p`
  font-size: 16px;
  line-height: 19px;
  margin-bottom: 10px;
  color: ${(props) => props.theme.blue100};
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
  background: ${(props) => props.theme.white_1};
  border: 1px solid ${(props) => props.theme.gray1000};
  border-radius: 6px;
`;

const Select = styled.button`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 11px 15px 10px;
  height: 38px;
`;

const Picked = styled.div`
  flex: 1;
  font-size: 16px;
  line-height: 19px;
  text-align: left;
  color: ${(props) => props.theme.black200};

  ${TextLimit};
`;

const Widgets = styled.div`
  display: flex;
  align-items: center;
  margin-left: 20px;
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  margin-right: 10px;
  transition: 0.3s;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 9px;
    height: 9px;

    path {
      fill: ${(props) => props.theme.main};
    }
  }
`;

const PickedLabel = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: 19px;
  margin-right: 10px;
  color: ${(props) => props.theme.gray700};
`;

const IconWrapper = styled.div<{ isOpen: boolean }>`
  svg {
    transition: all 0.3s ease-in-out;
    transform: rotate(0deg);

    path {
      fill: ${(props) => props.theme.black200};
    }
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      svg {
        transform: rotate(180deg);

        path {
          fill: ${(props) => props.theme.main};
        }
      }
    `}
`;

const Content = styled.div<{ isOpen: boolean }>`
  z-index: 5;
  pointer-events: none;
  position: absolute;
  padding: 10px 15px;
  top: 40px;
  width: 100%;
  max-height: 0;
  overflow-y: auto;
  transition: all 0.3s ease-in-out;
  background: ${(props) => props.theme.white_1};
  opacity: 0;
  border: 1px solid ${(props) => props.theme.gray1000};
  box-shadow: ${(props) => props.theme.blackShadow100};
  ${ScrollStyles};

  ${({ isOpen }) =>
    isOpen &&
    css`
      pointer-events: auto;
      opacity: 1;
      max-height: 280px;
      transition: all 0.3s ease-in-out;
    `}
`;
